import * as React from 'react';
import { createRoot } from 'react-dom/client';

import * as dxConfig from 'devextreme/core/config';
import { licenseKey } from '../../../_common/libs/dx25/ts/devextreme-license';

import { AppDispatcher } from './dispatcher/AppDispatcher';

import { AppActions } from './actions/AppActions';
import { WorkspaceActions } from './actions/WorkspaceActions';

import { AppStore } from './stores/AppStore';

import { AuthStore } from './stores/AuthStore';
import { DirsStore } from './stores/DirsStore';
import { WorkspaceEditStore } from './stores/WorkspaceEditStore';
import { WorkspaceStore } from './stores/WorkspaceStore';
import { TemplatesDirsStore } from './stores/TemplatesDirsStore';
import { WorkspacesDirsStore } from './stores/WorkspacesDirsStore';
import { TemplateEditStore } from './stores/TemplateEditStore';
import { WorkspacesNavStore } from './stores/WorkspacesNavStore';
import { DialogsStore } from './stores/DialogsStore';
import { NotificationsStore } from './stores/NotificationsStore';
import { SettingsStore } from './stores/SettingsStore';
import { WidgetEditStore } from './stores/WidgetEditStore';
import { PermissionsStore } from './stores/PermissionsStore';
import { ThemeStore } from './stores/ThemeStore';
import { ToolbarButtonsStore } from './stores/ToolbarButtonsStore';

import { apiCall, AQLQueries, aqlSetGlobObj } from '../../../_common/platform/ts/AqlUtility';

import { EntityModelsStore } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';

import * as appTheme from '../../../_common/platform/ts/appTheme';
import { HelpTools } from '../../../_common/platform/tsx/helpTools/HelpTools';

import * as initialRouter from './initialRouter';
import { ServerEvents } from './ServerEvents';

import Module from '../tsx/Module';

import { config } from './config';
import { initExportImport } from './../tsx/exportImport/exportImport';

import { BootstrapTheme } from './themes/bootstrapTheme';

import { init as workspaceTheme } from './themes/workspaceTheme';
import { init as widgetTheme } from './themes/widgetTheme';
import { init as commonTheme } from './themes/commonTheme';

import { WidgetBase } from '../../../_common/platform/ts/WidgetBase';
import * as Globalization from '../../../_common/platform/ts/Globalization';

const FastClick = require('pdc-platform-fastclick');

var _FastClick: { attach: any } = FastClick;
_FastClick.attach(document.body);

declare var window: any;
declare var require: any;

import { IGlob } from './Glob';
/* Init jquery */
const touchPunch = require('pdc-platform-touch-punch');
const jqueryUI = require('jquery-ui');

jqueryUI;
touchPunch;
/* End Init jQuery */

require('../sass-main/pdc-platform.sass');
require('../../../_common/libs/pdc-icons/icons.css');

// // Automatic icons fonts generation
// // Uncomment to generate CSS for icons
// require('../../../_common/libs/pdc-icons/icons.font');

export interface IGlobalObject extends IGlob {
    _prevGlob?: IGlobalObject;
}

export function getGlobalObj() {
    var obj: IGlobalObject = {};

    obj.AppDispatcher = AppDispatcher.get();

    obj.AppActions = AppActions.get(obj.AppDispatcher);
    obj.WorkspaceActions = WorkspaceActions.get(obj.AppDispatcher);

    obj.AuthStore = AuthStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.mainRouter = initialRouter.get(obj, obj.AppActions, obj.initialRouterExtender);

    obj.AppStore = AppStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.DirsStore = DirsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.WorkspaceEditStore = WorkspaceEditStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.WorkspaceStore = WorkspaceStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.AQLQueries = new AQLQueries(obj);

    obj.ToolbarButtonsStore = ToolbarButtonsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.TemplatesDirsStore = TemplatesDirsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.WorkspacesDirsStore = WorkspacesDirsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.TemplateEditStore = TemplateEditStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.WorkspacesNavStore = WorkspacesNavStore.get(obj, obj.AppDispatcher, obj.AppActions);

    DialogsStore.init(obj);

    obj.NotificationsStore = NotificationsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.SettingsStore = SettingsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.WidgetEditStore = WidgetEditStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.PermissionsStore = PermissionsStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.ThemeStore = ThemeStore.get(obj, obj.AppDispatcher, obj.AppActions);

    obj.appTheme = appTheme;
    obj.helpTools = new HelpTools();

    EntityModelsStore.init(obj);

    return obj;
}

if (window.pdc == null) {
    window.pdc = {};
}

if (window.pdc.test == null) {
    window.pdc.test = {};
}

if (window.pdc.platform == null) {
    window.pdc.platform = {};
}

export function init(container, customInitializer?) {
    const dxLicenseKey = {
        licenseKey: licenseKey
    };

    dxConfig.default(dxLicenseKey as any); //EVG: Without 'as any' it is not compiled to me.

    const glob = getGlobalObj();

    const serverEvents = new ServerEvents(glob);
    glob.ServerEvents = serverEvents;

    if (typeof customInitializer === 'function') {
        customInitializer(glob);
    }

    Globalization.loadMessages();

    glob.AppActions.appSettingsGetDefaultTheme();

    BootstrapTheme.init(glob);
    workspaceTheme(glob);
    widgetTheme(glob);
    commonTheme(glob);

    //pageVisibilityChange.init(glob);

    WidgetBase.initThemes(glob);

    if (config.ENV === 'dev') {
        window.pdc.test.glob = glob;
    }

    if (window.pdc.platform.glob != null) {
        console.log('Warning! window.pdc.platform.glob already exists');

        glob._prevGlob = window.pdc.platform.glob;
    }

    window.pdc.platform.glob = glob;
    aqlSetGlobObj(glob);

    Globalization.applyCulture(Globalization.getDefaultCulture());

    const root = createRoot(container);

    const componentRef = root.render(
        React.createElement(<React.ComponentClass<any>>Module, {
            glob: glob
        })
    );

    glob.AppStore.initBackboneHistory(glob.mainRouter);
    
    initExportImport(glob);

    return componentRef;
}
